// Owned by mju-ai-workbench/shared/schemas/homePrefs.ts — a person's Home preferences as they follow them between workbenches (D426). The AIOE session mirrors it to mju-aioe/packages/shared/src/schemas/homePrefs.ts; the workbench never mirrors it back. Keep the two identical.
/**
 * What `PUT /me/v1/preferences/home` carries and `GET` answers: the shape the workbench keeps as `FeedPrefs`
 * (shared/home/prefsModel.ts), checked at the person's home (the organisation, Mojo Up AI Cloud or a team hub).
 *
 * Deliberately lenient where the workbench grows: feed kinds and dock places are names, not a closed list, and unknown fields
 * pass through, so a newer workbench is never refused by an older home. Bounded everywhere, so a record stays small (the
 * home refuses one over 64 KB serialised). The workbench reads what comes back through its own `normalisePrefs`.
 *
 * Imports only zod.
 */
import { z } from "zod";
/** The serialised size a home keeps at most, in bytes. */
export const HOME_PREFS_MAX_BYTES = 64 * 1024;
const name = z.string().min(1).max(60);
const id = z.string().min(1).max(400);
const ms = z.number().int().nonnegative();
export const homePrefsSchema = z
    .object({
    /** How much the person wants each kind of card: -3 to 3, by kind name. */
    weights: z.record(name, z.number().int().min(-3).max(3)),
    /** Pinned card ids, first pinned first. */
    pinned: z.array(id).max(1000),
    /** Cards hidden until a time, or until what they say changes. */
    hidden: z.record(id, z.object({ until: ms, changeKey: z.string().max(400) }).passthrough()),
    /** Apps on the dock, in order. */
    pins: z.array(name).max(24),
    /** Starred apps, first first. */
    favourites: z.array(name).max(200),
    /** Cards put away as done, by id, with what they said and when. */
    done: z.record(id, z.object({ digest: z.string().max(400), at: ms }).passthrough()).optional(),
    /** The dock's places in the person's order, and which are taken off it. */
    dock: z.object({ order: z.array(name).max(100), hidden: z.array(name).max(100) }).passthrough().optional(),
    /** The Getting started checklist: hidden by the person, or finished. */
    gettingStarted: z.enum(["hidden", "finished"]).optional(),
    /** The Try these scenarios hidden by the person. */
    scenariosHidden: z.boolean().optional(),
})
    .passthrough();
/** `GET /me/v1/preferences/home`. */
export const homePrefsRecordSchema = z.object({
    rev: z.number().int().nonnegative(),
    prefs: homePrefsSchema.nullable(),
    updatedAt: z.string().nullable(),
});
/** `PUT /me/v1/preferences/home`. */
export const homePrefsPutSchema = z.object({ prefs: homePrefsSchema, baseRev: z.number().int().nonnegative().optional() });
//# sourceMappingURL=homePrefs.js.map